import { useEffect, useRef, useState } from 'react';

import type { TicketDetail } from '@nightshift/ui-types';

import { MarkdownComposer } from '../../components/markdown-composer.tsx';
import { Spinner } from '../../components/primitives.tsx';
import { api } from '../../live/api.ts';
import { useStore } from '../../live/context.tsx';
import { decideBriefCleanup } from '../../lib/brief-cleanup.ts';
import { isTicketDraftConflict, reconcileTicketDraft, ticketDraftSnapshot } from '../../lib/ticket-draft.ts';
import { registerUnsavedEditor } from '../../lib/unsaved.ts';
import { DraftConflictWarning } from './draft-conflict.tsx';
import { FootprintSection } from './footprint.tsx';
import { ReadinessSection } from './readiness.tsx';

/** Problem and acceptance on the ticket row. Operators can edit after create. */
export function BriefTab({
  detail,
  now,
  onReloaded,
}: {
  detail: TicketDetail;
  now: number;
  onReloaded: () => Promise<void>;
}) {
  const store = useStore();
  const [problem, setProblem] = useState(detail.ticket.problem);
  const [acceptance, setAcceptance] = useState(detail.ticket.acceptanceCriteria);
  const [busy, setBusy] = useState(false);
  const [cleanupState, setCleanupState] = useState<'idle' | 'queued' | 'processing' | 'message' | 'error'>('idle');
  const [cleanupMessage, setCleanupMessage] = useState('');
  const [conflict, setConflict] = useState(false);
  const ticketKeyRef = useRef(detail.ticket.ticketKey);
  const draftVersionRef = useRef(detail.ticket.version);
  const draftFieldsRef = useRef({
    problem: detail.ticket.problem,
    acceptanceCriteria: detail.ticket.acceptanceCriteria,
  });
  const dirtyRef = useRef(false);
  const problemRef = useRef(problem);
  const acceptanceRef = useRef(acceptance);
  const cleanupSequenceRef = useRef(0);
  const activeCleanupRef = useRef<{ id: number; key: string } | null>(null);

  problemRef.current = problem;
  acceptanceRef.current = acceptance;

  function invalidateCleanup() {
    cleanupSequenceRef.current += 1;
    const active = activeCleanupRef.current;
    activeCleanupRef.current = null;
    if (active) void api.cancelBriefCleanup(active.key, active.id).catch(() => undefined);
    setCleanupState('idle');
    setCleanupMessage('');
  }

  useEffect(() => {
    const refresh = reconcileTicketDraft({
      dirty: dirtyRef.current,
      draftTicketKey: ticketKeyRef.current,
      draftVersion: draftVersionRef.current,
      incomingTicketKey: detail.ticket.ticketKey,
      incomingVersion: detail.ticket.version,
      draftSnapshot: ticketDraftSnapshot(
        draftFieldsRef.current.problem,
        draftFieldsRef.current.acceptanceCriteria,
      ),
      incomingSnapshot: ticketDraftSnapshot(detail.ticket.problem, detail.ticket.acceptanceCriteria),
    });
    ticketKeyRef.current = detail.ticket.ticketKey;
    if (refresh !== 'reset') {
      if (refresh === 'conflict') {
        invalidateCleanup();
        setConflict(true);
      }
      return;
    }
    invalidateCleanup();
    dirtyRef.current = false;
    draftVersionRef.current = detail.ticket.version;
    draftFieldsRef.current = {
      problem: detail.ticket.problem,
      acceptanceCriteria: detail.ticket.acceptanceCriteria,
    };
    setConflict(false);
    setProblem(detail.ticket.problem);
    setAcceptance(detail.ticket.acceptanceCriteria);
  }, [detail.ticket.ticketKey, detail.ticket.version, detail.ticket.problem, detail.ticket.acceptanceCriteria]);

  useEffect(
    () => registerUnsavedEditor({ isDirty: () => dirtyRef.current, what: 'the ticket brief' }),
    [],
  );

  useEffect(() => () => {
    cleanupSequenceRef.current += 1;
    const active = activeCleanupRef.current;
    if (active) void api.cancelBriefCleanup(active.key, active.id).catch(() => undefined);
  }, []);

  async function save() {
    if (conflict) return;
    invalidateCleanup();
    setBusy(true);
    try {
      await api.updateTicket(detail.ticket.ticketKey, {
        problem,
        acceptanceCriteria: acceptance,
        expectedVersion: draftVersionRef.current,
        expectedFields: draftFieldsRef.current,
      });
      store.toast('good', 'Brief saved');
      dirtyRef.current = false;
      setConflict(false);
      await onReloaded();
    } catch (error) {
      if (isTicketDraftConflict(error)) setConflict(true);
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function cleanUp() {
    const key = detail.ticket.ticketKey;
    const submittedProblem = problemRef.current;
    const submittedAcceptance = acceptanceRef.current;
    const sequence = cleanupSequenceRef.current + 1;
    cleanupSequenceRef.current = sequence;
    setCleanupState('queued');
    setCleanupMessage('Queued for a runner…');
    try {
      const created = await api.createBriefCleanup(key, {
        problem: submittedProblem,
        acceptanceCriteria: submittedAcceptance,
        ticketVersion: draftVersionRef.current,
        storedProblem: draftFieldsRef.current.problem,
        storedAcceptance: draftFieldsRef.current.acceptanceCriteria,
      });
      if (cleanupSequenceRef.current !== sequence) {
        void api.cancelBriefCleanup(key, created.requestId).catch(() => undefined);
        return;
      }
      activeCleanupRef.current = { id: created.requestId, key };
      for (;;) {
        await new Promise((resolve) => window.setTimeout(resolve, 750));
        if (cleanupSequenceRef.current !== sequence) return;
        const status = await api.briefCleanup(key, created.requestId);
        if (cleanupSequenceRef.current !== sequence) return;
        const decision = decideBriefCleanup({
          status,
          submittedProblem,
          submittedAcceptance,
          currentProblem: problemRef.current,
          currentAcceptance: acceptanceRef.current,
        });
        if (decision.kind === 'pending') {
          setCleanupState(decision.state);
          setCleanupMessage(decision.message);
          continue;
        }
        activeCleanupRef.current = null;
        if (decision.kind === 'discard') return;
        if (decision.kind === 'message') {
          setCleanupState(decision.tone === 'error' ? 'error' : 'message');
          setCleanupMessage(decision.message);
          return;
        }
        if (!dirtyRef.current) {
          draftVersionRef.current = detail.ticket.version;
          draftFieldsRef.current = {
            problem: detail.ticket.problem,
            acceptanceCriteria: detail.ticket.acceptanceCriteria,
          };
        }
        dirtyRef.current = true;
        setProblem(decision.problem);
        if (decision.acceptance !== undefined) setAcceptance(decision.acceptance);
        setCleanupState('message');
        setCleanupMessage(decision.message);
        return;
      }
    } catch (error) {
      if (cleanupSequenceRef.current !== sequence) return;
      activeCleanupRef.current = null;
      setCleanupState('error');
      setCleanupMessage(`${error instanceof Error ? error.message : String(error)} Your draft is unchanged.`);
    }
  }

  return (
    <div className="drawer-brief">
      {conflict ? <DraftConflictWarning field="brief" /> : null}
      <div className="drawer-brief-editor">
        <section>
          <h3 className="drawer-section-kicker">Problem</h3>
          <MarkdownComposer
            id="ticket-problem"
            value={problem}
            rows={8}
            disabled={busy}
            placeholder="What is wrong, and why does it matter?"
            project={detail.ticket.projectSlug}
            enableMentions
            onChange={(next) => {
              invalidateCleanup();
              if (!dirtyRef.current) {
                draftVersionRef.current = detail.ticket.version;
                draftFieldsRef.current = {
                  problem: detail.ticket.problem,
                  acceptanceCriteria: detail.ticket.acceptanceCriteria,
                };
              }
              dirtyRef.current = true;
              setProblem(next);
            }}
          />
        </section>
        <section>
          <h3 className="drawer-section-kicker">Acceptance</h3>
          <MarkdownComposer
            id="ticket-acceptance"
            value={acceptance}
            rows={6}
            disabled={busy}
            placeholder="What must be true before this ticket is done?"
            project={detail.ticket.projectSlug}
            enableMentions
            onChange={(next) => {
              invalidateCleanup();
              if (!dirtyRef.current) {
                draftVersionRef.current = detail.ticket.version;
                draftFieldsRef.current = {
                  problem: detail.ticket.problem,
                  acceptanceCriteria: detail.ticket.acceptanceCriteria,
                };
              }
              dirtyRef.current = true;
              setAcceptance(next);
            }}
          />
        </section>
        <div className="drawer-brief-actions">
          <button
            type="button"
            className="btn"
            disabled={busy || conflict || problem.trim().length === 0 || cleanupState === 'queued' || cleanupState === 'processing'}
            onClick={() => void cleanUp()}
          >
            {cleanupState === 'queued' || cleanupState === 'processing' ? <Spinner /> : null} Clean Up
          </button>
          {cleanupState === 'queued' || cleanupState === 'processing' ? (
            <button type="button" className="btn btn-quiet" onClick={invalidateCleanup}>Cancel</button>
          ) : null}
          <button type="button" className="btn btn-accent" disabled={busy || conflict} onClick={() => void save()}>
            {busy ? <Spinner /> : null} Save brief
          </button>
        </div>
        {cleanupState !== 'idle' ? (
          <p className={cleanupState === 'error' ? 'form-error' : 'dim'} role={cleanupState === 'error' ? 'alert' : 'status'}>
            {cleanupMessage}{cleanupState === 'error' ? ' ' : null}
            {cleanupState === 'error' ? <button type="button" className="link-button" onClick={() => void cleanUp()}>Retry</button> : null}
          </p>
        ) : null}
      </div>
      <aside className="drawer-brief-aside">
        <ReadinessSection
          ticketKey={detail.ticket.ticketKey}
          ticketVersion={detail.ticket.version}
          readiness={detail.readiness}
          decisions={detail.decisions}
          now={now}
          onReloaded={onReloaded}
        />
        <FootprintSection
          suggestions={detail.suggestions}
          ticketKey={detail.ticket.ticketKey}
          ticketVersion={detail.ticket.version}
          now={now}
          onReloaded={onReloaded}
        />
      </aside>
    </div>
  );
}
